import { useRemember } from "@inertiajs/react";
import { SetStateAction, useState } from "react";

export type StorageOptions = {
    secondsTillExpiry?: number;
};

// Clear expired items on page load
const keys = Object.keys(localStorage);
for (const expiryKey of keys) {
    if (expiryKey.endsWith("-expiry")) {
        const expiry = localStorage.getItem(expiryKey);
        if (expiry && Number(expiry) < Date.now()) {
            localStorage.removeItem(expiryKey.replace("-expiry", ""));
            localStorage.removeItem(expiryKey);
        }
    }
}

const Storage = {
    remove: (key: string) => {
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}-expiry`);
    },
    load: <T>(key: string, defaultValue: T): T => {
        const str = localStorage.getItem(key);
        if (str) {
            const expiry = localStorage.getItem(`${key}-expiry`);
            if (expiry && Number(expiry) < Date.now()) {
                return defaultValue;
            }
            try {
                return JSON.parse(str);
            } catch (_e) {
                return defaultValue;
            }
        }
        return defaultValue;
    },
    loadAsString: (key: string): string | null => {
        return localStorage.getItem(key);
    },
    save: <T>(key: string, value: T, options: StorageOptions = {}) => {
        localStorage.setItem(key, JSON.stringify(value));
        if (options.secondsTillExpiry) {
            localStorage.setItem(
                `${key}-expiry`,
                String(Date.now() + options.secondsTillExpiry * 1000),
            );
        }
    },
    usePersist: <t>(
        key: string,
        defaultValue: t,
        options: StorageOptions = {},
    ): [t, (value: t) => void] => {
        const [state, setState] = useRemember<t>(
            Storage.load(key, defaultValue),
            key,
        );
        const setAndPersist = (value: t) => {
            setState(value);
            Storage.save(key, value, options);
        };
        return [state, setAndPersist];
    },
    useReactPersist: <t>(
        key: string,
        defaultValue: t,
        options: StorageOptions = {},
    ): [t, (value: SetStateAction<t>) => void] => {
        const [state, setState] = useState<t>(Storage.load(key, defaultValue));
        const setAndPersist = (setStateAction: SetStateAction<t>) => {
            setState((oldValue) => {
                const newValue =
                    setStateAction instanceof Function
                        ? setStateAction(oldValue)
                        : setStateAction;
                Storage.save(key, newValue, options);
                return newValue;
            });
        };
        return [state, setAndPersist];
    },
};

export default Storage;
